import { SearchBar } from '@common/components/SearchBar';
import { Title } from '@common/components/Title';
import {
    UseFilterStateResult,
    UsePaginationResult,
    UseSearchResult,
    UseSortingResult,
} from '@common/hooks';
import { NavigationTabsOption } from '@common/types/link';
import { APIFacetField } from '@common/types/queryState';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { FeatureConfiguration } from '@features/tableEngine/types';
import { Box, Card, CardProps, Flex, Grid, Inset } from '@radix-ui/themes';
import { ColumnDef } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelection, useTable, UseTableProps } from '../hooks';
import { createSelectionColumn } from './createSelectionColumn';
import { SelectionRow } from './SelectionRow';
import { TableColumnControlPanel } from './TableColumnControlPanel';
import { TableDisplay } from './TableDisplay';
import { TablePaginationControl } from './TablePaginationControl';
import { TableTabControl } from './TableTabControl';

interface TableCardProps<T extends object, TTab extends string = string> {
    data: T[];
    itemCount: number;
    pageCount: number;

    searchState: UseSearchResult;
    paginationState: UsePaginationResult;
    sortingState: UseSortingResult;

    facet?: APIFacetField[];
    filterState?: UseFilterStateResult;
    renderFacets?: (
        data: APIFacetField[],
        state: UseFilterStateResult,
    ) => React.ReactNode;

    tabOptions?: NavigationTabsOption<TTab>[];
    // tabState?: UseTabsResult<TTab>;

    config: Partial<FeatureConfiguration<T>>;

    isLoading?: boolean;
    isFetching?: boolean;

    maxRows?: number;

    size?: CardProps['size'];
}

const PAGE_SIZES = [...Array(5)].map((_, i) => (i + 1) * 10);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEFAULT_FEATURE_CONFIGURATION: FeatureConfiguration<any> = {
    search: { enabled: true },
    pagination: { enabled: true, pageSizes: PAGE_SIZES },
    sorting: { enabled: true },
    selection: { enabled: true, displayKey: 'id', summaryKey: undefined },
    facets: { enabled: false },
    modes: { enabled: false },
    columnControl: {
        enabled: true,
    },
    columnResizing: { enabled: true },
    columns: [],
};

export const TableCard = <
    TData extends { id: string },
    TTab extends string = string,
>({
    data,
    itemCount,
    pageCount,
    searchState,
    paginationState,
    sortingState,
    facet: facetData,
    filterState,
    renderFacets,
    tabOptions,
    // tabState,
    config,
    isLoading,
    isFetching,
    maxRows = 10,
    size = '5',
}: TableCardProps<TData, TTab>) => {
    const { t } = useTranslation('table');

    const selectionState = useSelection<TData>();
    const {
        title,
        columns,
        search,
        pagination,
        sorting,
        selection,
        facets,
        columnControl,
        columnResizing,
    } = mergeWithDefault(
        config,
        DEFAULT_FEATURE_CONFIGURATION,
    ) as FeatureConfiguration<TData>;

    const finalColumns: ColumnDef<TData>[] = useMemo(() => {
        const result: ColumnDef<TData>[] = [];

        if (selection.enabled) result.push(createSelectionColumn<TData>());
        result.push(...columns);

        return result;
    }, [columns, selection.enabled]);

    const smartTableArgs: UseTableProps<TData> = {
        data,
        columns: finalColumns,
        rowCount: itemCount,
        pageCount,
    };

    if (search.enabled) smartTableArgs.searchState = searchState;
    if (pagination.enabled) smartTableArgs.paginationState = paginationState;
    if (sorting.enabled) smartTableArgs.sortingState = sortingState;
    if (selection.enabled) smartTableArgs.selectionState = selectionState;

    const { table } = useTable(smartTableArgs);

    React.useEffect(() => {
        if (columnControl.columnOrder) {
            table.setColumnOrder(columnControl.columnOrder);
        }
    }, [columnControl.columnOrder, table]);

    const ROW_HEIGHT = 46;

    // const showFacets = facets.enabled && !!facetState;
    const showFacets = facets?.enabled && !!facetData && !!filterState;

    const tableViewportHeight = (maxRows + 1) * ROW_HEIGHT + 1;
    const gridColumns = showFacets ? '18vw 1fr' : '1fr';

    return (
        <Card variant={'surface'} size={size} style={{ height: '100%' }}>
            <Grid columns={gridColumns} gap={'0'} height={'100%'}>
                {/* Facet panel */}
                {showFacets && (
                    <Inset
                        side={'all'}
                        style={{
                            flexShrink: '0',
                            minWidth: '0',
                        }}
                    >
                        <Box
                            width={'18vw'}
                            p={'0'}
                            m={'0'}
                            height={'0'}
                            minHeight={'100%'}
                            overflow={'hidden'}
                            style={{
                                borderRight: '1px solid var(--gray-a5)',
                            }}
                        >
                            {renderFacets?.(facetData, filterState)}
                        </Box>
                    </Inset>
                )}

                {/* Table content side */}
                <Flex
                    direction={'column'}
                    gap={'2'}
                    style={{
                        flexGrow: 1,
                        minWidth: 0,
                    }}
                >
                    {title && (
                        <Box flexShrink={'0'}>
                            <Title>{title}</Title>
                        </Box>
                    )}

                    <Flex justify={'between'} flexShrink={'0'}>
                        <Box width={'500px'}>
                            {search.enabled && (
                                <SearchBar
                                    name='search' // ? Might need to contextualize this field
                                    size={'3'}
                                    placeholder={
                                        search.placeholder ??
                                        t('search.placeholder')
                                    }
                                    {...searchState}
                                />
                            )}
                        </Box>
                    </Flex>

                    <Flex justify={'between'}>
                        {selection.enabled && (
                            <SelectionRow
                                table={table}
                                selectionState={selectionState}
                                selection={selection}
                            />
                        )}

                        <Flex gap={'3'} align={'center'}>
                            {tabOptions && (
                                <TableTabControl options={tabOptions} />
                            )}
                            {columnControl.enabled && (
                                <TableColumnControlPanel table={table} />
                            )}
                        </Flex>
                    </Flex>

                    <Box
                        height={`${tableViewportHeight}px`}
                        flexGrow={'1'}
                        position={'relative'}
                    >
                        <TableDisplay
                            table={table}
                            isSortingEnabled={sorting.enabled}
                            isResizingEnabled={columnResizing.enabled}
                            isLoading={isLoading || isFetching}
                        />
                    </Box>

                    {pagination.enabled && (
                        <Flex
                            align={'stretch'}
                            justify={'center'}
                            flexShrink={'0'}
                        >
                            <TablePaginationControl
                                table={table}
                                pageSizes={pagination.pageSizes!}
                                isPaginated={true}
                            />
                        </Flex>
                    )}
                </Flex>
            </Grid>
        </Card>
    );
};
